import React, { useState, useEffect } from 'react';
import { Box, Container, useTheme, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import RecipeSearch from './RecipeSearch.js';
import RecipeList from './RecipeList.js';
import useRecipeFilters from './UseRecipeFilters.js';
import * as styles from './styles.js';
import * as Icons from '@mui/icons-material';

function Recipes({ recipes, inventory, togglePossession, favorites, bookmarks, toggleFavorite, toggleBookmark, originFilter }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Add state for the active ingredient set
  const [activeIngredientSet, setActiveIngredientSet] = useState(null);
  
  // Check if search terms were passed via navigation state
  const initialSearchTerms = location.state?.searchTerms || (() => {
    const stored = localStorage.getItem('recipeSearchTerms');
    return stored ? JSON.parse(stored) : [];
  });

  const [searchInput, setSearchInput] = useState("");
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [sortOrder, setSortOrder] = useState(() => {
    const stored = localStorage.getItem('recipeSortOrder');
    return stored || 'asc';
  });
  const [statusFilters, setStatusFilters] = useState(() => {
    const stored = localStorage.getItem('recipeStatusFilters');
    return stored ? JSON.parse(stored) : [];
  });
  const [originFilters, setOriginFilters] = useState(() => {
    const stored = localStorage.getItem('recipeOriginFilters');
    return stored ? JSON.parse(stored) : originFilter ? [originFilter] : [];
  });
  const [filterHeight, setFilterHeight] = useState(0);

  // Clear the navigation state after using it
  useEffect(() => {
    if (location.state?.searchTerms) {
      const newState = { ...location.state };
      delete newState.searchTerms;
      navigate(location.pathname, { state: newState, replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  // Safely store search terms
  useEffect(() => {
    if (searchTerms.length > 0) {
      localStorage.setItem('recipeSearchTerms', JSON.stringify(searchTerms));
    } else {
      localStorage.removeItem('recipeSearchTerms');
    }
  }, [searchTerms]);

  // Safely store other filters
  useEffect(() => {
    localStorage.setItem('recipeSortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    if (statusFilters.length > 0) {
      localStorage.setItem('recipeStatusFilters', JSON.stringify(statusFilters));
    } else {
      localStorage.removeItem('recipeStatusFilters');
    }
  }, [statusFilters]);

  useEffect(() => {
    if (originFilters.length > 0) {
      localStorage.setItem('recipeOriginFilters', JSON.stringify(originFilters));
    } else {
      localStorage.removeItem('recipeOriginFilters');
    }
  }, [originFilters]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (originFilter && originFilters.length > 0) {
      setOriginFilters([]);
      localStorage.setItem('recipeOriginFilters', JSON.stringify([]));
    }
  }, [originFilter]);

  const { filteredRecipes, uniqueOrigins, nameCounts } = useRecipeFilters({
    recipes,
    inventory,
    searchTerms,
    statusFilters,
    originFilters,
    favorites,
    originFilter,
    activeIngredientSet,
  });

  // Handle set change from the SetSelector
  const handleSetChange = (selectedSet) => {
    setActiveIngredientSet(selectedSet);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        pt: {
          xs: `${Math.max(filterHeight + 30, 160)}px`,
          sm: `${Math.max(filterHeight + 30, 160)}px`,
        },
        pb: 10,
      }}
    >
      <Box sx={styles.fixedHeader}>
        <Container maxWidth="md" disableGutters sx={styles.container}>
          <RecipeSearch
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            searchTerms={searchTerms}
            setSearchTerms={setSearchTerms}
            debouncedSearchInput={debouncedSearchInput}
            statusFilters={statusFilters}
            setStatusFilters={setStatusFilters}
            originFilters={originFilters}
            setOriginFilters={setOriginFilters}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            navigate={navigate}
            originFilter={originFilter}
            uniqueOrigins={uniqueOrigins}
            recipes={recipes}
            inventory={inventory}
            onFilterHeightChange={setFilterHeight}
            onSetChange={handleSetChange}
          />
        </Container>
      </Box>
      {originFilter === 'My Recipes' && (
        <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Icons.Add />}
            onClick={() => navigate('/recipe/new')}
            sx={{ textTransform: 'none' }}
          >
            Add Recipe
          </Button>
        </Box>
      )}
      <RecipeList
        filteredRecipes={filteredRecipes}
        sortOrder={sortOrder}
        inventory={inventory}
        favorites={favorites}
        bookmarks={bookmarks}
        toggleFavorite={toggleFavorite}
        toggleBookmark={toggleBookmark}
        nameCounts={nameCounts}
        searchTerms={searchTerms}
      />
    </Box>
  );
}

export default Recipes;